import React, { useState, useRef, useEffect, useMemo } from "react";
import classNames from "classnames";
import plus from "@/assets/plus.svg";
import minus from "@/assets/minus.svg";
import styles from "./index.module.scss";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";

export type SituableForDatasetEntry = {
  brand: { id: string; name: string };
  model: { name: string; id: string };
  modification: { id: string; name: string };
  yearFrom: string;
  yearTo: string;
};

export type SituableForProps = {
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  data: SituableForDatasetEntry[];
};

type DataTree = Record<
  string,
  {
    id: string;
    name: string;
    models: Record<
      string,
      {
        id: string;
        name: string;
        modifications: Record<
          string,
          {
            id: string;
            name: string;
            yearFrom: string;
            yearTo: string;
          }
        >;
      }
    >;
  }
>;

type DataList = Array<{
  id: string;
  name: string;
  models: Array<{
    id: string;
    name: string;
    modifications: Array<{
      id: string;
      name: string;
      yearFrom: string;
      yearTo: string;
    }>;
  }>;
}>;

const SituableFor: React.FC<SituableForProps> = ({ type = LayoutStyleTypes.TYPE_LG, data }) => {
  const [toggled, setToggled] = useState<string[]>([]);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  const groups = useMemo(() => {
    const brands: DataTree = {};
    for (const item of data) {
      brands[item.brand.id] ??= {
        id: item.brand.id,
        name: item.brand.name,
        models: {},
      };
      brands[item.brand.id].models[item.model.id] ??= {
        id: item.model.id,
        name: item.model.name,
        modifications: {},
      };
      brands[item.brand.id].models[item.model.id].modifications[item.modification.id] ??= {
        id: item.modification.id,
        name: item.modification.name,
        yearFrom: item.yearFrom,
        yearTo: item.yearTo,
      };
    }
    const result: DataList = Object.values(brands).map((brand) => ({
      ...brand,
      models: Object.values(brand.models).map((model) => ({
        ...model,
        modifications: Object.values(model.modifications),
      })),
    }));
    for (const brand of result) {
      brand.models.sort((a, b) => a.name.localeCompare(b.name));
      for (const model of brand.models) {
        model.modifications.sort((a, b) => a.name.localeCompare(b.name));
      }
    }
    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [data]);

  const collapsable = true;

  useEffect(() => {
    if (!collapsable) return;
    for (const [id, el] of Object.entries(refs.current)) {
      if (el) {
        const height = el.getBoundingClientRect().height;
        el.parentElement?.style.setProperty("--height", `${height}px`);
      }
    }
  }, [groups, toggled, collapsable]);

  const handleToggle = (id: string) => {
    setToggled((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <div className={classNames(styles.root, styles[type])}>
      {groups.map((brand) => {
        const opened = toggled.includes(brand.id);
        return (
          <div key={brand.id} className={classNames(styles.container, { [styles.closed]: !opened, [styles.collapsable]: collapsable })}>
            <div ref={(el) => (refs.current[brand.id] = el)} className={styles.segment} onClick={() => handleToggle(brand.id)}>
              <div className={styles.left}>
                <Icon className={styles.button} src={opened ? minus : plus} />
                <div className={styles.name}>{brand.name}</div>
              </div>
              {brand.models.map((model) => (
                <div className={styles.model} key={model.id}>
                  <div className={styles.name}>{model.name}</div>
                  <div className={styles.modifications}>
                    {model.modifications.map((mod) => (
                      <div className={styles.modification} key={mod.id}>
                        <div className={styles.name}>{mod.name}</div>
                        <div className={styles.year}>
                          ({mod.yearFrom}-{mod.yearTo})
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SituableFor;
